import { Injectable } from '@angular/core';
import { FormIssue } from '@interfaces/form-issue';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import { map, take } from 'rxjs/operators';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';

/** The counts a Move Locations affiliation preview carries - see IMoveLocationsAffiliationPreview. */
type MoveAffiliationCounts = {
  destinationAffiliateName: string;
  tanksGainingAffiliation: number;
  tanksLosingAffiliation: number;
  tanksChangingAffiliation: number;
  tanksUnchanged: number;
};

@Injectable()
export class ConfirmationDialogService {
  dialogRef: MatDialogRef<ConfirmationDialogComponent, any>;
  resourceKey: any = {
    Ok: 'Dialog.Ok',
    Confirm: 'Dialog.Confirm',
    Yes: 'Dialog.Yes',
    No: 'Dialog.No',
    Save: 'Dialog.Save',
    Cancel: 'Dialog.Cancel',
    Create: 'Dialog.Create',
    Dismiss: 'Dialog.Dismiss',
    Restore: 'Dialog.Restore',
    ThemeDialogConfirmationTitle: 'Dialog.ThemeDialogConfirmationTitle',
    ThemeDialogConfirmationContent: `Dialog.ThemeDialogConfirmationContent`,
    ThemeDialogConfirmationCancelButton: 'Dialog.ThemeDialogConfirmationCancelButton',
    ThemeDialogConfirmationCreateButton: 'Dialog.ThemeDialogConfirmationCreateButton',
    UnsavedChangesPrompt: 'Dialog.UnsavedChangesPrompt',
    GTSPromptTitle: 'Dialog.GTSPromptTitle',
    GTSPromptContent: 'Dialog.GTSPromptContent',
    DismissSelectedNotifications: 'Dialog.DismissSelectedNotifications',
    RestoreAllNotificationsTitle: 'Dialog.RestoreAllNotificationsTitle',
    ConfirmCancelChanges: 'Dialog.ConfirmCancelChanges',
    ConfirmUpdateNow: 'Dialog.ConfirmUpdateNow',
    ConfirmUpdateInventoryUnits: 'Dialog.ConfirmUpdateInventoryUnits',
    LocationMoveReminderTitle: 'MoveForms.LocationMove.ReminderTitle',
    LocationMoveReminderMessage: 'MoveForms.LocationMove.ReminderMessage',
    ConfirmInventoryStatusChange: 'TankHistory.AddInventory.ConfirmationStatusChange',
  };

  constructor(
    public translate: TranslateService,
    public dialog: MatDialog
  ) {}

  public showModelIsDirtyDialog(): Observable<boolean> {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.Confirm),
        content: this.translate.instant(this.resourceKey.UnsavedChangesPrompt),
        noButtonLabel: this.translate.instant(this.resourceKey.No),
        yesButtonLabel: this.translate.instant(this.resourceKey.Yes),
        usersChoice: false,
      },
    });

    return this.getConfirmationResult();
  }

  public showCreateThemeDialog(): Observable<boolean> {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.ThemeDialogConfirmationTitle),
        content: this.translate.instant(this.resourceKey.ThemeDialogConfirmationContent),
        noButtonLabel: this.translate.instant(this.resourceKey.ThemeDialogConfirmationCancelButton),
        yesButtonLabel: this.translate.instant(
          this.resourceKey.ThemeDialogConfirmationCreateButton
        ),
      },
    });

    return this.getConfirmationResult();
  }

  public showGlobalTankSearchDialog() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.GTSPromptTitle),
        content: this.translate.instant(this.resourceKey.GTSPromptContent),
        yesButtonLabel: this.translate.instant(this.resourceKey.Ok),
      },
    });

    return this.getConfirmationResult();
  }

  public showDismissdNotificationsDialog() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.DismissSelectedNotifications),
        content: null,
        noButtonLabel: this.translate.instant(this.resourceKey.Cancel),
        yesButtonLabel: this.translate.instant(this.resourceKey.Dismiss),
      },
    });

    return this.getConfirmationResult();
  }

  public showRestoreAllNotifications() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.RestoreAllNotificationsTitle),
        content: null,
        noButtonLabel: this.translate.instant(this.resourceKey.Cancel),
        yesButtonLabel: this.translate.instant(this.resourceKey.Restore),
      },
    });

    return this.getConfirmationResult();
  }

  public cancelChanges() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.ConfirmCancelChanges),
        content: null,
        noButtonLabel: this.translate.instant(this.resourceKey.No),
        yesButtonLabel: this.translate.instant(this.resourceKey.Yes),
      },
    });

    return this.getConfirmationResult();
  }

  public confirmBulkLImportLocation() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Bulk Creation Location',
        content:
          'Confirm to create Locations under the selected Organization using the imported file.',
        noButtonLabel: this.translate.instant(this.resourceKey.ThemeDialogConfirmationCancelButton),
        yesButtonLabel: this.translate.instant(
          this.resourceKey.ThemeDialogConfirmationCreateButton
        ),
      },
    });

    return this.getConfirmationResult();
  }

  public updateNow() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.ConfirmUpdateNow),
        content: null,
        noButtonLabel: this.translate.instant(this.resourceKey.No),
        yesButtonLabel: this.translate.instant(this.resourceKey.Yes),
      },
    });

    return this.getConfirmationResult();
  }

  /** Moving a report that is behind past its missed runs: those runs are never sent, so it asks first. */
  public skipMissedReportRuns() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('ReportSettings.Generate.Health.SkipConfirmTitle'),
        content: this.translate.instant('ReportSettings.Generate.Health.SkipConfirmContent'),
        noButtonLabel: this.translate.instant(this.resourceKey.No),
        yesButtonLabel: this.translate.instant('ReportSettings.Generate.Health.Skip'),
      },
    });

    return this.getConfirmationResult();
  }

  public UpdateInventoryUnits() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.ConfirmUpdateInventoryUnits),
        content: null,
        noButtonLabel: this.translate.instant(this.resourceKey.No),
        yesButtonLabel: this.translate.instant(this.resourceKey.Yes),
      },
    });

    return this.getConfirmationResult();
  }

  private getConfirmationResult(): Observable<boolean> {
    // Backdrop click / Escape / the overlay's own close call afterClosed() with undefined - not
    // false - because no button set a choice. Treated as "Yes" before, so clicking away from the
    // "unsaved changes" prompt silently allowed navigation. A dismissed confirmation must read the
    // same as its negative button, so only an explicit `true` (the Yes/Ok button) counts.
    return this.dialogRef.afterClosed().pipe(
      take(1),
      map((result: boolean | undefined) => result === true)
    );
  }

  public showMoveLocationReminderDialog() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.resourceKey.LocationMoveReminderTitle),
        content: this.translate.instant(this.resourceKey.LocationMoveReminderMessage),
        noButtonLabel: null,
        yesButtonLabel: this.translate.instant(this.resourceKey.Ok),
      },
    });

    return this.getConfirmationResult();
  }

  /**
   * One sentence for each group of tanks a pending move affects, in a fixed order. Shared by the
   * preview at the bottom of the move form and the prompt on Move, so the two cannot say different
   * things. Callers only ask when at least one tank's affiliation would change.
   *
   * Returned as key + params rather than finished text. `app-tap-alert` renders every list entry
   * through the translate pipe, so handing it a finished sentence sends that sentence back through
   * the lookup, misses, and CommonMissingTranslationHandler returns it wrapped in square brackets.
   * `FormIssue` is the shape the alert already accepts for a key that carries interpolation; with no
   * `fieldId` it renders as plain text rather than a field link.
   */
  public describeMoveAffiliationChanges(preview: MoveAffiliationCounts): FormIssue[] {
    const lines: FormIssue[] = [];

    if (preview.tanksGainingAffiliation > 0) {
      lines.push({
        messageKey: 'MoveForms.LocationMove.Affiliation.Gaining',
        translationParams: {
          count: preview.tanksGainingAffiliation,
          affiliate: preview.destinationAffiliateName,
        },
      });
    }

    if (preview.tanksChangingAffiliation > 0) {
      lines.push({
        messageKey: 'MoveForms.LocationMove.Affiliation.Changing',
        translationParams: {
          count: preview.tanksChangingAffiliation,
          affiliate: preview.destinationAffiliateName,
        },
      });
    }

    if (preview.tanksLosingAffiliation > 0) {
      lines.push({
        messageKey: 'MoveForms.LocationMove.Affiliation.Losing',
        translationParams: { count: preview.tanksLosingAffiliation },
      });
    }

    if (preview.tanksUnchanged > 0) {
      lines.push({
        messageKey: 'MoveForms.LocationMove.Affiliation.Unchanged',
        translationParams: { count: preview.tanksUnchanged },
      });
    }

    return lines;
  }

  /**
   * The same sentences resolved to text, for the prompt on Move - that dialog interpolates its
   * content as a plain string and does no lookup of its own.
   */
  private resolveMoveAffiliationChanges(preview: MoveAffiliationCounts): string[] {
    return this.describeMoveAffiliationChanges(preview).map(line =>
      this.translate.instant(line.messageKey, line.translationParams)
    );
  }

  /**
   * Affiliation is re-resolved from the destination organization during a move rather than chosen,
   * so tanks can gain, lose or swap an affiliate as a side effect of picking a destination. This
   * confirms that side effect before it happens. Only shown when something actually changes.
   */
  public confirmAffiliationChanges(preview: MoveAffiliationCounts): Observable<boolean> {
    const lines = this.resolveMoveAffiliationChanges(preview);

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('MoveForms.LocationMove.Affiliation.ConfirmTitle'),
        //The dialog interpolates content as text, so line breaks would not render - each count is a
        //sentence and they read as one paragraph.
        content: lines.join(' '),
        noButtonLabel: this.translate.instant(this.resourceKey.Cancel),
        // OK, not Yes: the prompt states what will happen rather than asking a question.
        yesButtonLabel: this.translate.instant(this.resourceKey.Ok),
      },
    });

    return this.getConfirmationResult();
  }

  /**
   * Retain / clear / cancel for a parent-organization change that carries an affiliation.
   *
   * Three outcomes out of a two-button dialog: Yes and No are the two real choices, and dismissing
   * the dialog cancels the save rather than silently picking one. Which two choices they are depends
   * on canRetain - keeping an affiliation whose affiliate is gone, inactive, unflagged or unreachable
   * is not on offer, because the org save would refuse it anyway.
   */
  public confirmParentChangeAffiliation(preview: {
    currentAffiliateName: string;
    canRetain: boolean;
    retainBlockedReason: string;
    tanksAffectedByClearing: number;
  }): Observable<'retain' | 'clear' | 'cancel'> {
    const canRetain = preview.canRetain === true;

    const content = canRetain
      ? this.translate.instant('OrganizationDetails.ParentChange.KeepOrClear', {
          affiliate: preview.currentAffiliateName,
          count: preview.tanksAffectedByClearing,
        })
      : this.translate.instant('OrganizationDetails.ParentChange.MustClear', {
          affiliate: preview.currentAffiliateName,
          reason: preview.retainBlockedReason,
          count: preview.tanksAffectedByClearing,
        });

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('OrganizationDetails.ParentChange.AffiliationTitle'),
        content: content,
        // Labelled for the action, not Yes/No - "Yes" to a question with two real outcomes tells the
        // user nothing about which one they picked.
        yesButtonLabel: canRetain
          ? this.translate.instant('OrganizationDetails.ParentChange.KeepButton')
          : this.translate.instant('OrganizationDetails.ParentChange.ClearButton'),
        noButtonLabel: canRetain
          ? this.translate.instant('OrganizationDetails.ParentChange.ClearButton')
          : this.translate.instant(this.resourceKey.Cancel),
      },
    });

    return this.dialogRef.afterClosed().pipe(
      take(1),
      map((choice: boolean | undefined) => {
        // Dismissed - no choice was made, so do not guess one.
        if (choice === undefined || choice === null) return 'cancel' as const;

        if (canRetain) return choice ? ('retain' as const) : ('clear' as const);

        // Retain was not on offer: Yes clears, No cancels.
        return choice ? ('clear' as const) : ('cancel' as const);
      })
    );
  }

  public confirmInventoryStatusChanges(
    activateCount: number,
    deactivateCount: number
  ): Observable<boolean> {
    const title = this.translate.instant('Dialog.Confirm'); // Or create a specific key like: 'Dialog.ConfirmInventoryStatusChange'
    const content = this.translate.instant(this.resourceKey.ConfirmInventoryStatusChange, {
      activateCount,
      deactivateCount,
    });

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: title,
        content: content,
        noButtonLabel: this.translate.instant(this.resourceKey.No),
        yesButtonLabel: this.translate.instant(this.resourceKey.Yes),
      },
    });

    return this.getConfirmationResult();
  }

  public confirmBulkTankUpdate() {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Bulk Tank Configuration Update',
        content:
          'Confirm to update Tank Configurations using the selected Excel file. This process will create an audit trail for each update.',
        noButtonLabel: this.translate.instant(this.resourceKey.Cancel),
        yesButtonLabel: this.translate.instant(this.resourceKey.Confirm),
      },
    });

    return this.getConfirmationResult();
  }

  public confirm(
    title: string,
    message: string,
    yesLabel: string,
    noLabel: string
  ): Observable<boolean> {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: title,
        content: message,
        noButtonLabel: noLabel,
        yesButtonLabel: yesLabel,
      },
    });

    return this.getConfirmationResult();
  }
}
